import React from 'react';

import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import * as yup from 'yup';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton/ColoredButton';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';
import validation_messages from 'messages/validation_messages';

import ButtonsGrid from '../_components/ButtonsGrid';
import DialogGrid from '../_components/DialogGrid';

const FORM_ID = 'AddSubareaForm';

type AddSubareaFormValues = {
  name: string;
};

type AddSubareaDialogProps = {
  open: boolean;
  onClose: () => void;
  areaId: string | number;
  onSuccess: (newSubareaId: number) => void;
};

const AddSubareaDialog: React.FC<AddSubareaDialogProps> = ({ open, onClose, areaId, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation(
    (values: AddSubareaFormValues) =>
      actionTemplatesApi.createSubarea({
        name: values.name,
        area: areaId,
      }),
    {
      onSuccess: data => {
        enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
        onSuccess(data.data.id);
        onClose();
      },
      onError: (err: { response?: { data?: Record<string, unknown> | string } }) => {
        const responseData = err?.response?.data;
        if (typeof responseData === 'object' && responseData !== null) {
          Object.values(responseData).forEach(msg => enqueueSnackbar(String(msg), { variant: 'error' }));
        } else {
          enqueueSnackbar(t(general_messages.api_error), { variant: 'error' });
        }
      },
    },
  );

  const formik = useFormik<AddSubareaFormValues>({
    initialValues: { name: '' },
    validationSchema: yup.object({
      name: yup.string().required(t(validation_messages.required)),
    }),
    onSubmit: values => mutation.mutate(values),
    enableReinitialize: true,
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const dialogActions = (
    <ButtonsGrid>
      <ColoredButton
        className={undefined}
        customColor='none'
        disabled={mutation.isLoading}
        icon={false}
        onClick={handleClose}
        showLoader={false}
        textLike={false}
        variant='outlined'
      >
        {t(general_messages.cancel)}
      </ColoredButton>
      <ColoredButton
        className={undefined}
        customColor='secondary'
        disabled={mutation.isLoading}
        form={FORM_ID}
        icon={false}
        showLoader={false}
        textLike={false}
        type='submit'
        variant='outlined'
      >
        {t(general_messages.save)}
      </ColoredButton>
    </ButtonsGrid>
  );

  return (
    <DialogGrid
      color='secondary'
      dialogActions={dialogActions}
      displayTitle
      fullWidth
      loading={false}
      loadingCover={false}
      noPadding={false}
      onClose={handleClose}
      open={open}
      scrollDisabled={false}
      sending={false}
      title={t(action_library_messages.add_subarea_dialog.title)}
      withCloseConfirmation={false}
    >
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <CenteredGrid gridGap={2} withoutPadding>
          <FormTextInput formik={formik} id='name' label={t(action_library_messages.add_subarea_dialog.name_label)} />
        </CenteredGrid>
      </form>
    </DialogGrid>
  );
};

export default AddSubareaDialog;
